import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from '../utils/toast';
import PasteUploadArea from './PasteUploadArea';
import UserSelector from './UserSelector';
import AutocompleteField from './AutocompleteField';
import { ArrowLeftIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { createVerificationRecord } from '../utils/documentVerification';
import { useAuth } from '../contexts/AuthContext';

const CreateMasterData = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    clientType: '',
    clientName: '',
    clientAddress: '',
    phoneNumber: {
      countryCode: '+91',
      number: ''
    },
    applicantType: '',
    country: '',
    state: '',
    region: '',
    pincode: '',
    partners: [],
    contactPersons: [{
      title: '',
      name: '',
      phoneNumbers: '',
      email: ''
    }],
    authorizedSignatories: [{
      title: '',
      name: '',
      age: '',
      designation: '',
      designationName: '',
      fatherName: '',
      email: '',
      phone: '',
      address: ''
    }],
    documents: {},
    isActive: true,
    createdBy: null
  });
  const [clientTypes, setClientTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [indiaStates, setIndiaStates] = useState([]);
  const [indiaRegions, setIndiaRegions] = useState([]);
  const [phoneCountryCodes, setPhoneCountryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadFields, setUploadFields] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);

  useEffect(() => {
    fetchClientTypes();
    fetchUploadFields();
    fetchCountries();
    fetchIndiaStates();
    fetchPhoneCountryCodes();
    console.log('Current user:', user); // Debug log
    if (user?.role === 'admin') {
      console.log('User is admin, fetching users...'); // Debug log
      fetchUsers();
    }
  }, [user]);

  const fetchClientTypes = async () => {
    try {
      const response = await api.get('/admin/client-types');
      setClientTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch client types');
    }
  };

  const fetchUploadFields = async () => {
    try {
      const response = await api.get('/admin/upload-fields');
      setUploadFields(response.data);
    } catch (error) {
      console.error('Failed to fetch upload fields');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get('/master-data/countries');
      setCountries(response.data.data);
    } catch (error) {
      console.error('Failed to fetch countries');
    }
  };

  const fetchIndiaStates = async () => {
    try {
      const response = await api.get('/master-data/india-states');
      setIndiaStates(response.data.data);
    } catch (error) {
      console.error('Failed to fetch India states');
    }
  };

  const fetchIndiaRegions = async (state) => {
    try {
      const response = await api.get(`/master-data/india-regions/${encodeURIComponent(state)}`);
      setIndiaRegions(response.data.data);
    } catch (error) {
      console.error('Failed to fetch India regions');
    }
  };

  const fetchPhoneCountryCodes = async () => {
    try {
      const response = await api.get('/master-data/phone-country-codes');
      setPhoneCountryCodes(response.data.data);
    } catch (error) {
      console.error('Failed to fetch phone country codes');
    }
  };

  const handleFileUpload = async (file, documentType, label) => {
    try {
      setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [documentType]: progress }));
        }
      });

      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: {
            fileName: file.name,
            fileUrl: response.data.url,
            uploadedAt: new Date(),
            uploadedBy: response.data.userId,
            label: label
          }
        }
      }));

      // Store pending verification for after document is saved
      const verificationData = {
        documentType: 'masterData',
        documentField: documentType,
        fileUrl: response.data.url,
        fileName: file.name,
        fileSize: file.size
      };

      setPendingVerifications(prev => [...prev, verificationData]);
      toast.success('File uploaded successfully');

      setUploadProgress(prev => ({ ...prev, [documentType]: 100 }));
    } catch (error) {
      console.error(`Failed to upload ${documentType}`, error);
      setErrors(prev => ({
        ...prev,
        [documentType]: 'Failed to upload file'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(formData);
    try {
      setLoading(true);

      const formattedValues = {
        ...formData,
        contactPersons: formData.contactPersons.map(contact => ({
          ...contact,
          phoneNumbers: contact.phoneNumbers.split(',').map(phone => phone.trim())
        })),
        authorizedSignatories: formData.authorizedSignatories,
        documents: formData.documents,
        createdBy: formData.createdBy?._id || null
      };

      const response = await api.post('/master-data', formattedValues);
      const masterDataId = response.data.data._id;
      
      // Create verification records for pending verifications
      if (pendingVerifications.length > 0) {
        try {
          for (const verificationData of pendingVerifications) {
            const verificationPayload = {
              ...verificationData,
              documentId: masterDataId
            };
            
            await api.post('/checklist/create-after-save', verificationPayload);
          }
          toast.success('Master data saved successfully. Verification records created.');
        } catch (verificationError) {
          console.error('Error creating verification records:', verificationError);
          toast.success('Master data saved successfully. Some verification records may not have been created.');
        }
      } else {
        toast.success('Master data saved successfully');
      }
      
      // Redirect to the created master data detail page
      navigate(`/master-data/${masterDataId}`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to save master data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, field, index = null, section = null) => {
    if (section === 'authorizedSignatories') {
      const newSignatories = [...formData.authorizedSignatories];
      newSignatories[index] = {
        ...newSignatories[index],
        [field]: e.target.value
      };
      setFormData(prev => ({
        ...prev,
        authorizedSignatories: newSignatories
      }));
    } else if (index !== null) {
      const newContactPersons = [...formData.contactPersons];
      newContactPersons[index] = {
        ...newContactPersons[index],
        [field]: e.target.value
      };
      setFormData(prev => ({
        ...prev,
        contactPersons: newContactPersons
      }));
    } else if (field === 'clientType') {
      // Handle clientType specially - store as object with _id
      const selectedClientType = clientTypes.find(type => type._id === e.target.value);
      setFormData(prev => ({
        ...prev,
        [field]: selectedClientType || '',
        // Initialize partners array if LLP or Partnership is selected
        partners: (selectedClientType?.name === 'LLP' || selectedClientType?.name === 'Partnership') 
          ? (prev.partners.length > 0 ? prev.partners : [{ name: '' }])
          : []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value
      }));
    }
  };

  const handleUserChange = (selectedUser) => {
    setFormData(prev => ({
      ...prev,
      createdBy: selectedUser
    }));
  };

  const handlePhoneNumberChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: {
        ...prev.phoneNumber,
        [field]: value
      }
    }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      country,
      state: '',
      region: ''
    }));
    setIndiaRegions([]);
  };

  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      state,
      region: ''
    }));
    if (state && formData.country === 'India') {
      fetchIndiaRegions(state);
    }
  };

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, { name: '' }]
    }));
  };

  const removePartner = (index) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.filter((_, i) => i !== index)
    }));
  };

  const handlePartnerChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.map((partner, i) => 
        i === index ? { ...partner, name: value } : partner
      )
    }));
  };

  const addContactPerson = () => {
    setFormData(prev => ({
      ...prev,
      contactPersons: [...prev.contactPersons, {
        title: '',
        name: '',
        phoneNumbers: '',
        email: ''
      }]
    }));
  };

  const addAuthorizedSignatory = () => {
    setFormData(prev => ({
      ...prev,
      authorizedSignatories: [...prev.authorizedSignatories, {
        title: '',
        name: '',
        age: '',
        designation: '',
        designationName: '',
        fatherName: '',
        email: '',
        phone: '',
        address: ''
      }]
    }));
  };

  const removeContactPerson = (index) => {
    setFormData(prev => ({
      ...prev,
      contactPersons: prev.contactPersons.filter((_, i) => i !== index)
    }));
  };

  const removeAuthorizedSignatory = (index) => {
    setFormData(prev => ({
      ...prev,
      authorizedSignatories: prev.authorizedSignatories.filter((_, i) => i !== index)
    }));
  };

  const renderFileUpload = (field) => {
    const document = formData.documents?.[field.name];
    const progress = uploadProgress[field.name];
    const error = errors[field.name];

    return (
      <div className="mb-4" key={field.name}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
        </label>
        <div className="flex items-center space-x-4">
          <PasteUploadArea
            onFileSelect={(file) => handleFileUpload(file, field.name, field.label)}
            accept={field.allowedTypes?.join(',') || "application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
            maxSize={field.maxSize || 5 * 1024 * 1024}
            className="flex-1"
            dragDropText="Upload a file"
            pasteText="or paste from clipboard (Ctrl+V)"
            uploadedUrl={document?.fileUrl || null}
          >
            
          </PasteUploadArea>
          
          {progress !== undefined && progress < 100 && (
            <div className="w-24">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
        {errors[field.name] && (
          <p className="mt-1 text-sm text-red-600">{errors[field.name]}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/master-data')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Create New Master Data</h1>
          </div>
          <p className="text-gray-600">Fill in the details below to create a new master data record.</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* User Selection for Admin */}
              {user?.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created By (Optional)
                  </label>
                  <UserSelector
                    selectedUser={formData.createdBy}
                    onUserChange={handleUserChange}
                    userList={users}
                    placeholder="Select a user to assign as creator..."
                  />
                </div>
              )}
              {/* Client Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="clientType"
                  value={formData.clientType?._id || ''}
                  onChange={(e) => handleInputChange(e, 'clientType')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Client Type</option>
                  {clientTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="clientName"
                  value={formData.clientName}
                  onChange={(e) => handleInputChange(e, 'clientName')}
                  placeholder="Enter client name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Client Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Address
                </label>
                <textarea
                  name="clientAddress"
                  value={formData.clientAddress}
                  onChange={(e) => handleInputChange(e, 'clientAddress')}
                  placeholder="Enter client address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              {/* Phone Number */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Country Code
                  </label>
                  <select
                    value={formData.phoneNumber.countryCode}
                    onChange={(e) => handlePhoneNumberChange('countryCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {phoneCountryCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber.number}
                    onChange={(e) => handlePhoneNumberChange('number', e.target.value)}
                    placeholder="Enter phone number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Applicant Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Applicant Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.applicantType}
                  onChange={(e) => handleInputChange(e, 'applicantType')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Applicant Type</option>
                  <option value="Individual/Sole Proprietor">Individual/Sole Proprietor</option>
                  <option value="StartUp">StartUp</option>
                  <option value="Small Enterprise">Small Enterprise</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Country, State, Region */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <AutocompleteField
                    label="Country"
                    value={formData.country}
                    onChange={handleCountryChange}
                    options={countries}
                    placeholder="Type to search country..."
                    required={true}
                  />
                </div>
                <div>
                  <AutocompleteField
                    label="State"
                    value={formData.state}
                    onChange={handleStateChange}
                    options={formData.country === 'India' ? indiaStates : []}
                    placeholder={formData.country === 'India' ? "Type to search state..." : "Select country first"}
                    required={true}
                    disabled={formData.country !== 'India'}
                  />
                </div>
                <div>
                  <AutocompleteField
                    label="Region"
                    value={formData.region}
                    onChange={(region) => handleInputChange({ target: { value: region } }, 'region')}
                    options={indiaRegions}
                    placeholder={formData.state ? "Type to search region..." : "Select state first"}
                    required={true}
                    disabled={!formData.state || formData.country !== 'India'}
                  />
                </div>
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={(e) => handleInputChange(e, 'pincode')}
                  placeholder="Enter pincode"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Partners - Only show for LLP or Partnership */}
              {(formData.clientType?.name === 'LLP' || formData.clientType?.name === 'Partnership') && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Partners</h3>
                  {formData.partners.map((partner, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Partner Name {index + 1}
                        </label>
                        <input
                          type="text"
                          value={partner.name}
                          onChange={(e) => handlePartnerChange(index, e.target.value)}
                          placeholder="Enter partner name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      {formData.partners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePartner(index)}
                          className="mt-6 text-red-600 hover:text-red-800 p-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addPartner}
                    className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                  >
                    Add Partner
                  </button>
                </div>
              )}

              

              {/* Contact Persons */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Contact Persons</h3>
                {formData.contactPersons.map((contact, index) => (
                  <div key={index} className="border border-gray-200 rounded-md p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <select
                          value={contact.title}
                          onChange={(e) => handleInputChange(e, 'title', index)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Title</option>
                          {['Mr', 'Miss', 'Mrs', 'Dr'].map(title => (
                            <option key={title} value={title}>{title}</option>
                          ))}
                        </select>
                      </div>
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) => handleInputChange(e, 'name', index)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Numbers</label>
                        <input
                          type="text"
                          value={contact.phoneNumbers}
                          onChange={(e) => handleInputChange(e, 'phoneNumbers', index)}
                          placeholder="Comma separated numbers"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div className="lg:col-span-1" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleInputChange(e, 'email', index)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                        {formData.contactPersons.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeContactPerson(index)}
                            className="text-red-600 hover:text-red-800 p-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addContactPerson}
                  className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                >
                  Add Contact Person
                </button>
              </div>

              {/* Authorized Signatories */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Authorized Signatories</h3>
                {formData.authorizedSignatories.map((signatory, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <select
                          value={signatory.title}
                          onChange={(e) => handleInputChange(e, 'title', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Title</option>
                          {['Mr', 'Miss', 'Mrs', 'Dr'].map(title => (
                            <option key={title} value={title}>{title}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={signatory.name}
                          onChange={(e) => handleInputChange(e, 'name', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                        <input
                          type="number"
                          min="0"
                          max="150"
                          value={signatory.age}
                          onChange={(e) => handleInputChange(e, 'age', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                        <input
                          type="text"
                          value={signatory.designation}
                          onChange={(e) => handleInputChange(e, 'designation', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Designation Name</label>
                        <input
                          type="text"
                          value={signatory.designationName}
                          onChange={(e) => handleInputChange(e, 'designationName', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={signatory.email}
                          onChange={(e) => handleInputChange(e, 'email', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input
                          type="text"
                          value={signatory.phone}
                          onChange={(e) => handleInputChange(e, 'phone', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Father Name</label>
                        <input
                          type="text"
                          value={signatory.fatherName}
                          onChange={(e) => handleInputChange(e, 'fatherName', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                          type="text"
                          value={signatory.address}
                          onChange={(e) => handleInputChange(e, 'address', index, 'authorizedSignatories')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    {formData.authorizedSignatories.length > 1 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeAuthorizedSignatory(index)}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addAuthorizedSignatory}
                  className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                >
                  Add Authorized Signatory
                </button>
              </div>

              {/* Document Upload Section */}
              {uploadFields.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Documents</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {uploadFields.map(field => renderFileUpload(field))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigate('/master-data')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateMasterData; 